import React, { useState, useMemo, useCallback } from "react";
import {
  Restaurant,
  AirlineSeatReclineNormal,
  CheckCircleOutline,
  DoDisturbOnOutlined,
  ArrowCircleRightOutlined,
  Accessibility,
  TaskAlt,
  FlightLand,
  FlightTakeoff,
  Place,
  MilitaryTech,
} from "@mui/icons-material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { Box, Chip, Typography } from "@mui/material";
import { parseDateTime } from "../../helpers";
import { getListViewRowClasses } from "../../helpers/passengerIconHelpers";
import {
  formatPassengerRewardDisplay,
  isRewardServiceCode,
} from "../../helpers/rewardTier";
import { specialNeedsBadges } from "../../helpers/ssrCategories";
import "../../style/list-view.scss";

export const ListView = ({
  list: passengerList,
  handleModalOpen,
  mealModeOn,
}) => {
  const [sortModel, setSortModel] = useState([]);

  // Filter out extra seats (CBBG and EXST)
  const filteredPassengers = useMemo(() => {
    return passengerList.filter(
      (passenger) =>
        passenger.passengerType !== "B" && passenger.passengerType !== "E"
    );
  }, [passengerList]);

  // Check if there are multiple destinations (multi-leg flight)
  const hasMultipleDestinations = useMemo(() => {
    const destinations = new Set();
    for (const passenger of filteredPassengers) {
      if (passenger.destination) {
        destinations.add(passenger.destination);
      }
    }
    return destinations.size > 1;
  }, [filteredPassengers]);

  const timeTokenComparator = (valueA, valueB) => {
    const extractMinutesFromToken = (value) => {
      if (!value) return Number.POSITIVE_INFINITY;
      const timeToken = value.split(" ")[2]?.slice(1);
      if (!timeToken) return Number.POSITIVE_INFINITY;
      const [hours, minutes] = timeToken.split(":").map((num) => Number(num));
      if (Number.isNaN(hours) || Number.isNaN(minutes))
        return Number.POSITIVE_INFINITY;
      return hours * 60 + minutes;
    };
    const minutesA = extractMinutesFromToken(valueA);
    const minutesB = extractMinutesFromToken(valueB);
    if (minutesA === minutesB) return 0;
    return minutesA - minutesB;
  };

  const buildMealSortKey = (row) => {
    const meal = row?.mealObject;
    const category = (meal?.category || "").toString();
    const code = (meal?.code || "").toString();
    if (!category && !code) return "";
    return category ? `${category}~${code}` : code;
  };

  const mealSortComparator = (_v1, _v2, cellParams1, cellParams2) => {
    const api = cellParams1?.api;
    const row1 = api?.getRow ? api.getRow(cellParams1?.id) : cellParams1?.row;
    const row2 = api?.getRow ? api.getRow(cellParams2?.id) : cellParams2?.row;
    const a = buildMealSortKey(row1);
    const b = buildMealSortKey(row2);
    if (a === b) return 0;
    if (a === "") return 1; // empty to bottom in asc
    if (b === "") return -1;
    return a.localeCompare(b, undefined, { sensitivity: "base" });
};

  const baseColumns = useMemo(
    () => [
      { field: "name", headerName: "Name" },
  {
        field: "passengerType",
        headerName: "Type",
        type: "string",
        renderCell: (params) => {
          if (!params || !params.row) return null;
          return params.row.isInfant ? "INF" : params.row.passengerType;
        },
  },
  {
        field: "seat",
        headerName: "Seat",
        type: "string",
    renderHeader: () => <AirlineSeatReclineNormal />,
  },
      ...(hasMultipleDestinations
        ? [
            {
              field: "destination",
              headerName: "Dest",
              type: "string",
              width: 70,
              renderHeader: () => <Place />,
              renderCell: (params) => {
                if (!params || !params.row) return null;
                const { destination, isDisembarking } = params.row;
                if (!destination) return null;

                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: isDisembarking
                        ? "rgba(255, 152, 0, 0.2)"
                        : "transparent",
                      borderRadius: "4px",
                      padding: "2px 6px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isDisembarking ? "bold" : "normal",
                        color: isDisembarking ? "#e65100" : "inherit",
                      }}
                    >
                      {destination}
                    </Typography>
                  </Box>
                );
              },
            },
          ]
        : []),
  {
        field: "status",
        headerName: "Status",
        type: "string",
    renderHeader: () => <TaskAlt />,
    renderCell: (params) => {
          if (!params || !params.row) return null;
      const { checkedIn, boarded } = params.row;

      if (!checkedIn) {
            return <DoDisturbOnOutlined style={{ color: "gray" }} />;
      } else if (checkedIn && !boarded) {
            return <ArrowCircleRightOutlined style={{ color: "blue" }} />;
      } else if (boarded) {
            return <CheckCircleOutline style={{ color: "green" }} />;
      }

      return null;
    },
  },
  {
        field: "mealObject",
        headerName: "Meal",
        type: "string",
    renderHeader: () => <Restaurant />,
        sortComparator: mealSortComparator,
        valueGetter: (value) => {
          const meal = value;
          const code = meal?.code || "";
          const category = meal?.category || "";
          const isSpecial = category === "Special";

          if (!code) return "";
          if (!category) return code;

          // For Special meals, show category and code
          // For Adult, Child, Upgraded - show only category
          return isSpecial ? `${category} ${code}` : category;
        },
        renderCell: (params) => {
          if (!params || !params.row) return null;
          const meal = params.row.mealObject;
          if (!meal || !meal.code) return null;

          const isSpecial = meal.category === "Special";

          // For Special meals, show category and code
          // For Adult, Child, Upgraded - show only category
          if (!meal.category) return <span>{meal.code}</span>;

          return (
            <span>
              {isSpecial ? `${meal.category} ${meal.code}` : meal.category}
            </span>
          );
        },
  },
  {
        field: "rewardDisplay",
        headerName: "Reward",
        type: "string",
        width: 180,
        renderHeader: () => <MilitaryTech />,
      },
      {
        field: "servicesDisplay",
        headerName: "Services",
        type: "string",
    renderHeader: () => <Accessibility />,
        renderCell: (params) => {
          if (!params?.row) return null;
          const badges = specialNeedsBadges(params.row);
          if (badges.length === 0) return params.row.servicesDisplay || null;
          const badgeCodes = new Set(badges.map((badge) => badge.code));
          const rest = String(params.row.servicesDisplay || "")
            .split(", ")
            .filter((code) => code && !badgeCodes.has(code.toUpperCase()))
            .join(", ");
          return (
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                flexWrap: "wrap",
                alignItems: "center",
                py: 0.5,
              }}
            >
              {badges.map((badge) => (
                <Box key={badge.code} component="span" title={badge.categoryLabel}>
                  <Chip label={badge.code} size="small" sx={{ height: 20 }} />
                </Box>
              ))}
              {rest ? <span>{rest}</span> : null}
            </Box>
          );
        },
    },
  {
        field: "inboundConnections",
        headerName: "Inbound",
        width: 230,
    renderHeader: () => <FlightLand />,
        valueGetter: (value) => {
          const inbound = Array.isArray(value) ? value[0] : null;
          if (!inbound) return "";
          let timeQualifierCode = "S";
          let timeString = "";

          if (inbound.arrivalTimeQualifier) {
            timeQualifierCode = inbound.arrivalTimeQualifier.charAt(0);
            timeString = parseDateTime(inbound.arrivalTime).time;
          } else if (inbound.actualArrivalTime) {
            timeQualifierCode = "A";
            timeString = parseDateTime(inbound.actualArrivalTime).time;
          } else if (inbound.estimatedArrivalTime) {
            timeQualifierCode = "E";
            timeString = parseDateTime(inbound.estimatedArrivalTime).time;
      } else if (inbound.arrivalTime) {
            timeQualifierCode = "S";
            timeString = parseDateTime(inbound.arrivalTime).time;
      }

          const standSuffix = inbound.arrivalStand
            ? ` ${inbound.arrivalStand}`
            : "";

          return `${inbound?.flightNumber} ${inbound?.departure}-${inbound?.destination} ${timeQualifierCode}${timeString}${standSuffix}`;
    },
        sortComparator: timeTokenComparator,
  },
  {
        field: "outboundConnections",
        headerName: "Outbound",
        width: 230,
    renderHeader: () => <FlightTakeoff />,
        valueGetter: (value) => {
          const outbound = Array.isArray(value) ? value[0] : null;
          if (!outbound) return "";
          let timeQualifierCode = "S";
          let timeString = "";

          if (outbound.departureTimeQualifier) {
            timeQualifierCode = outbound.departureTimeQualifier.charAt(0);
            timeString = parseDateTime(outbound.departureTime).time;
          } else if (outbound.actualDepartureTime) {
            timeQualifierCode = "A";
            timeString = parseDateTime(outbound.actualDepartureTime).time;
          } else if (outbound.estimatedDepartureTime) {
            timeQualifierCode = "E";
            timeString = parseDateTime(outbound.estimatedDepartureTime).time;
      } else if (outbound.departureTime) {
            timeQualifierCode = "S";
            timeString = parseDateTime(outbound.departureTime).time;
      }

          const gateInfo = outbound.departureGate
            ? ` ${outbound.departureGate}`
            : outbound.gate
            ? ` ${outbound.gate}`
            : "";

          return `${outbound?.flightNumber} ${outbound?.departure}-${outbound?.destination} ${timeQualifierCode}${timeString}${gateInfo}`;
    },
        sortComparator: timeTokenComparator,
  },
    ],
    [hasMultipleDestinations]
  );

  const [columns, setColumns] = useState(baseColumns);

  React.useEffect(() => {
    setColumns((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return baseColumns;
      return baseColumns.map((col) => {
        const prevCol = prev.find((c) => c.field === col.field);
        return prevCol && typeof prevCol.width === "number"
          ? { ...col, width: prevCol.width }
          : col;
      });
    });
  }, [baseColumns]);

  const handleColumnResizeCommit = useCallback((params) => {
    const { colDef, width } = params;
    setColumns((prevColumns) =>
      prevColumns.map((col) =>
        col.field === colDef.field ? { ...col, width } : col
      )
    );
  }, []);

  const formatServicesForDisplay = useCallback((passengers) => {
    return passengers.map((passenger) => {
      const sortedServiceCodes = (passenger.services ?? [])
        .map((service) =>
          typeof service === "string" ? service : service?.code
        )
        .filter((code) => {
          if (!code) return false;
          if (code === passenger.frequentFlyerTierLevel) return false;
          return !isRewardServiceCode(code);
        })
        .sort((a, b) => a.localeCompare(b))
        .join(", ");

      return {
        ...passenger,
        servicesDisplay: sortedServiceCodes,
        rewardDisplay: formatPassengerRewardDisplay(passenger),
      };
    });
  }, []);

  const rowData = useMemo(
    () => formatServicesForDisplay(filteredPassengers),
    [filteredPassengers, formatServicesForDisplay]
  );

  return (
    <DataGridPro
      autoHeight
      sortingOrder={["asc", "desc"]}
      rows={rowData}
      columns={columns}
      sortModel={sortModel}
      onSortModelChange={(model) =>
        setSortModel(Array.isArray(model) ? model : [])
      }
      onRowClick={({ row }) => {
        const passengerModal =
          filteredPassengers.find((p) => p.id === row.id) || row;
        handleModalOpen(passengerModal);
      }}
      onColumnResize={handleColumnResizeCommit}
      initialState={{
        pagination: {
          paginationModel: { page: 0, pageSize: 100 },
        },
      }}
      pageSizeOptions={[10000]}
      getRowClassName={(params) => {
        return getListViewRowClasses(params.row);
      }}
      getRowHeight={() => "auto"}
      disableSelectionOnClick
      hideFooterSelectedRowCount
      sx={{
        "& .MuiDataGrid-row.Mui-selected": {
          backgroundColor: "inherit",
        },
        "& .MuiDataGrid-row:not(.meal-received-row):hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
        "& .MuiDataGrid-row.meal-received-row:hover": {
          backgroundColor: "#c0e0b0 !important",
        },
        "& .MuiDataGrid-cell:focus": {
          outline: "none",
        },
        "& .MuiDataGrid-cell": {
          display: "flex",
          alignItems: "center",
        },
        "& .MuiDataGrid-row:focus": {
          outline: "none",
        },
      }}
    />
  );
};
